import { AfterViewInit, DestroyRef, Directive, ElementRef, EventEmitter, inject, NgZone, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

// Treat the view as "at the bottom" within this many px (sub-pixel rounding, zoom) — a
// tight margin, so returning to the bottom resumes following but stopping short does not.
const AT_BOTTOM_SLACK = 16;
// Show the scroll-to-bottom button only once there is a meaningful amount below the fold.
const CAN_SCROLL_DOWN_THRESHOLD = 100;

/**
 * Keeps a scroll container pinned to the bottom as its content grows (e.g. a
 * streaming chat answer), and stops following the moment the user scrolls up to
 * read — resuming when they return to the bottom.
 *
 * It watches both the content (a MutationObserver, which also catches late/async
 * reflow a lifecycle hook runs too early to see) and the viewport height (a
 * ResizeObserver, for when surrounding UI shrinks/grows the list). Observing
 * actual changes — rather than re-pinning on every change-detection pass — is
 * what lets the user scroll freely once the content settles.
 *
 * Following pauses only on a genuine upward scroll (the user moving the view up,
 * by any means — wheel, drag, scrollbar or keyboard), recognised as scrollTop
 * moving up and landing away from the bottom. Content streaming in grows the
 * height without moving scrollTop, and a clamp from a resize, a DPI change (e.g.
 * moving the window to another screen) or a collapsing indicator always lands
 * back at the bottom; none of those is the user, so none pauses following.
 *
 * It emits {@link canScrollDownChange} so the host can show a "scroll to bottom"
 * affordance, and exposes {@link scrollToBottom} for that button and for "a new
 * message was sent".
 */
@Directive({
    selector: '[pStickToBottom]',
    standalone: true,
    exportAs: 'pStickToBottom'
})
export class StickToBottomDirective implements AfterViewInit {
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly ngZone = inject(NgZone);

    private following = true;
    private lastCanScrollDown = false;
    private layoutCheckScheduled = false;
    private layoutCheckRaf = 0;
    // The previous scroll position, to tell a user scroll-up (scrollTop drops and lands
    // away from the bottom) from our own re-pin or a clamp (which lands at the bottom).
    private lastScrollTop = 0;

    // TODO(Angular 17.3+): replace the EventEmitter output with output<boolean>()
    /** Emits whether there is content below the fold (so the host can offer a scroll-to-bottom button). */
    @Output() public readonly canScrollDownChange = new EventEmitter<boolean>();

    public ngAfterViewInit(): void {
        const element = this.host.nativeElement;

        // The observers and the input listeners run outside Angular so streaming —
        // which fires them continuously — does not trigger a change-detection pass
        // per event. We only re-enter the zone when the button's visibility flips.
        this.ngZone.runOutsideAngular(() => {
            // Both observers funnel through a single rAF-coalesced check: a streamed
            // answer fires the MutationObserver many times per frame, and we want one
            // measure + pin per frame, not a forced reflow per token.
            const onChange = (): void => this.scheduleLayoutCheck();
            const resizeObserver = new ResizeObserver(onChange);
            resizeObserver.observe(element);

            const mutationObserver = new MutationObserver(onChange);
            mutationObserver.observe(element, { childList: true, subtree: true, characterData: true });

            this.destroyRef.onDestroy(() => {
                resizeObserver.disconnect();
                mutationObserver.disconnect();
                cancelAnimationFrame(this.layoutCheckRaf);
            });

            // Position-based truth: a genuine scroll-up pauses, returning to the bottom resumes.
            fromEvent(element, 'scroll')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.onScroll());
        });
    }

    /** Resume following and bring the latest content into view (the FAB and "message sent"). */
    public scrollToBottom(): void {
        this.following = true;
        this.host.nativeElement.scrollTo({ top: this.host.nativeElement.scrollHeight, behavior: 'smooth' });
    }

    private onScroll(): void {
        const scrollTop = this.host.nativeElement.scrollTop;
        const atBottom = this.isWithin(AT_BOTTOM_SLACK);

        // Pause only on a real scroll-up: scrollTop moved up AND the view is now away from
        // the bottom. A clamp (a resize, a DPI change, a collapsing indicator) always lands
        // back at the bottom, and streamed content grows the height without moving scrollTop
        // — so neither is read as a scroll-up.
        if (scrollTop < this.lastScrollTop && !atBottom) {
            this.following = false;
        } else if (atBottom) {
            this.following = true;
        }

        this.lastScrollTop = scrollTop;
        this.updateCanScrollDown();
    }

    /** Collapse a burst of layout changes within a frame into a single {@link onLayoutChange}. */
    private scheduleLayoutCheck(): void {
        if (this.layoutCheckScheduled) {
            return;
        }
        this.layoutCheckScheduled = true;
        this.layoutCheckRaf = requestAnimationFrame(() => {
            this.layoutCheckScheduled = false;
            this.onLayoutChange();
        });
    }

    private onLayoutChange(): void {
        if (this.following) {
            const element = this.host.nativeElement;

            // Assigning past scrollHeight lets the browser clamp to the true bottom.
            element.scrollTop = element.scrollHeight;

            // Record the pinned position so its own scroll event is not read as a scroll-up.
            this.lastScrollTop = element.scrollTop;
        }

        this.updateCanScrollDown();
    }

    /**
     * Whether the viewport's bottom edge is within `slack` px of the content's end.
     *
     *   ┌─────────────────┐
     *   │   hidden above  │
     *   ├─────────────────┤ ← scrollTop                  (px scrolled from the top)
     *   │                 │
     *   │  visible area   │   clientHeight               (viewport height)
     *   │                 │
     *   ├─────────────────┤ ← scrollTop + clientHeight   (bottom edge of the viewport)
     *   │  hidden below   │   ≤ `slack` px to count as "at the bottom"
     *   └─────────────────┘ ← scrollHeight               (total content height)
     *
     * i.e. true when scrollTop + clientHeight >= scrollHeight - slack. Called with
     * AT_BOTTOM_SLACK (a few px) to decide whether to keep following, and with
     * CAN_SCROLL_DOWN_THRESHOLD (100px) to decide whether to offer the button.
     */
    private isWithin(slack: number): boolean {
        const element = this.host.nativeElement;
        return element.scrollTop + element.clientHeight >= element.scrollHeight - slack;
    }

    private updateCanScrollDown(): void {
        const element = this.host.nativeElement;
        const canScrollDown = !this.isWithin(CAN_SCROLL_DOWN_THRESHOLD) && element.scrollHeight > element.clientHeight;

        if (canScrollDown !== this.lastCanScrollDown) {
            this.lastCanScrollDown = canScrollDown;
            this.ngZone.run(() => this.canScrollDownChange.emit(canScrollDown));
        }
    }
}
